import React, { createRef, useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { UploadError } from '@ff-os/shared-react/components/file-uploader/FileUploader';
import { UploadMethodsWindow } from '@ff-os/shared-react/components/upload-methods/UploadMethodsWindow';
import { WindowStackItem } from '@ff-os/shared-react/context/window-stack/WindowStackItem';
import { useScrollIntoView } from '@ff-os/shared-react/hooks/useScrollIntoView';
import { selectIsPhotoFunProcessing } from '@ff-scene-plugins/photo-prints-redux/slices/generalUiSlice';
import { selectFailedUploadsCount, selectIsNewGroup, selectSuccessfulUploadsCount } from '@ff-scene-plugins/photo-prints-redux/slices/groupsUiSlice';
import { PhotoPrintsPdpDispatch, PhotoPrintsPdpState } from '@ff-scene-plugins/photo-prints-redux/store';
import classNames from 'classnames';
import { selectIsEmptyGroup } from '../../../_redux-groups';
import { selectActivePaperOptionForVariantId } from '../../../_redux-papers';
import { selectActiveSizeOptionForVariantId } from '../../../_redux-sizes';
import { createPhotoPrintsUploadMethods } from '../../../createPhotoPrintsUploadMethods';
import { uploadImages } from '../../../thunks/uploadImages';
import { incrementWithScheduledReset, updateWithScheduledReset } from '../../../thunks/withScheduledReset';
import { VariantOption } from '../../../VariantOption';
import { ChangeVariantWindow } from '../../change-variant-window/ChangeVariantWindow';
import { PhotoPrintsGroupWindow } from '../photo-print-group-window/PhotoPrintsGroupWindow';
import { EmptyPhotoPrintsGroupPreview } from './EmptyPhotoPrintsGroupPreview';
import { NotEmptyPhotoPrintsGroupPreview } from './NotEmptyPhotoPrintsGroupPreview';
import styles from './PhotoPrintsGroupPreview.module.scss';

const _PhotoPrintsGroupPreview = ({
    groupId, variantId, isNewGroup, isEmpty, onError,
    upload, successfulUploadCount, failedUploadsCount,
    isPhotoFunProcessing
}: Props) => {

    const [isDetailWindowOpen, setDetailWindowOpen] = useState(false);
    const [isDetailFotofunWindowOpen, setDetailFotofunWindowOpen] = useState(false);
    const [isCreateWindowOpen, setCreateWindowOpen] = useState(false);
    const [isUploadWindowOpen, setUploadWindowOpen] = useState(false);

    const panelClassName = classNames(styles.panel, {
        [styles.success]: successfulUploadCount > 0,
        [styles.error]: failedUploadsCount > 0
    });

    const ref = createRef<HTMLDivElement>();
    const scrollIntoView = useScrollIntoView(ref, { includeHidden: true });

    useEffect(() => {
        if (successfulUploadCount > 0 || failedUploadsCount > 0 || isNewGroup) {
            scrollIntoView();
        }
    }, [successfulUploadCount, failedUploadsCount, isNewGroup, scrollIntoView]);

    const openUploadWindow = useCallback(() => setUploadWindowOpen(true), [setUploadWindowOpen]);
    const closeUploadWindow = useCallback(() => setUploadWindowOpen(false), [setUploadWindowOpen]);

    const openCreateWindow = useCallback(() => setCreateWindowOpen(true), [setCreateWindowOpen]);
    const closeCreateWindow = useCallback(() => setCreateWindowOpen(false), [setCreateWindowOpen]);

    const closeDetailWindow = useCallback(() => setDetailWindowOpen(false), [setDetailWindowOpen]);

    const closeDetailFotofunWindow = useCallback(() => setDetailFotofunWindowOpen(false), [setDetailFotofunWindowOpen]);

    const onUpload = () => {
        closeUploadWindow();
        setDetailWindowOpen(true);
    };

    const onChange = (files?: File[]) => {
        if (files) {
            onUpload();
            upload(files);
        }
    };

    const methods = createPhotoPrintsUploadMethods({
        dropzone: {
            onChange: onChange,
            onError: onError
        },
        externalUpload: {
            groupId: groupId,
            onChange: onUpload
        }
    });

    return (
        <div ref={ref}>
            {!isEmpty ? (
                <NotEmptyPhotoPrintsGroupPreview className={panelClassName}
                    failedUploadsCount={failedUploadsCount}
                    successfulUploadsCount={successfulUploadCount}
                    setDetailWindowOpen={setDetailWindowOpen}
                    setDetailFotofunWindowOpen={setDetailFotofunWindowOpen}
                    browse={openUploadWindow}
                    groupId={groupId} variantId={variantId} />
            ) : (
                <EmptyPhotoPrintsGroupPreview className={panelClassName}
                    start={openCreateWindow}
                    groupId={groupId} />
            )}

            {isDetailWindowOpen && (
                <WindowStackItem>
                    <PhotoPrintsGroupWindow
                        close={closeDetailWindow}
                        groupId={groupId}
                        variantId={variantId}
                        isPhotoFunProcessing={isPhotoFunProcessing || false}
                        successfulUploadCount={successfulUploadCount} />
                </WindowStackItem>
            )}

            {isDetailFotofunWindowOpen && (
                <WindowStackItem>
                    <PhotoPrintsGroupWindow
                        close={closeDetailFotofunWindow}
                        groupId={groupId}
                        variantId={variantId}
                        fotofun={true}
                        isPhotoFunProcessing={isPhotoFunProcessing || false}
                        successfulUploadCount={successfulUploadCount} />
                </WindowStackItem>
            )}

            {isCreateWindowOpen && (
                <WindowStackItem>
                    <ChangeVariantWindow withUploader close={closeCreateWindow} groupId={groupId} />
                </WindowStackItem>
            )}

            <UploadMethodsWindow isOpen={isUploadWindowOpen}
                close={closeUploadWindow}
                methods={methods} />
        </div>
    );
};

type OwnProps = {
    groupId: string;
    variantId: string;
};

type Props = OwnProps & {
    size: VariantOption;
    paper: VariantOption;
    onError: (errors: UploadError[]) => void;
    isEmpty: boolean;
    upload: (files: File[]) => void;
    successfulUploadCount: number;
    failedUploadsCount: number;
    isNewGroup: boolean;
    isPhotoFunProcessing?: boolean;
};

const mapStateToProps = (state: PhotoPrintsPdpState, ownProps: OwnProps) => ({
    size: selectActiveSizeOptionForVariantId(state, ownProps.variantId)!,
    paper: selectActivePaperOptionForVariantId(state, ownProps.variantId)!,
    isEmpty: selectIsEmptyGroup(state, ownProps.groupId),
    isNewGroup: selectIsNewGroup(state, ownProps.groupId),
    successfulUploadCount: selectSuccessfulUploadsCount(state, ownProps.groupId),
    failedUploadsCount: selectFailedUploadsCount(state, ownProps.groupId),
    isPhotoFunProcessing: selectIsPhotoFunProcessing(state)
});

const mapDispatchToProps = (dispatch: PhotoPrintsPdpDispatch, ownProps: OwnProps) => ({
    upload: (files: File[]) => dispatch(uploadImages(files, ownProps.groupId)),
    onError: (errors: UploadError[]) => {
        dispatch(incrementWithScheduledReset(ownProps.groupId, 'failedUploadsCount', errors.length));
        dispatch(updateWithScheduledReset({ id: ownProps.groupId, key: 'failedUploadsError', value: errors }));
    }
});

export const PhotoPrintsGroupPreview = connect(
    mapStateToProps,
    mapDispatchToProps
)(_PhotoPrintsGroupPreview);